<template>
  <div id="SelectionList" class="page selection-list-page">
    <back-header v-if="platform !== 'web'" title="返回">
      <template #bread>
        <div class="bread">已选产品</div>
      </template>
    </back-header>
    <div v-else class="web-page-header">已选产品</div>
    <template v-if="pageLoadOver">
      <div v-if="!isLogin" class="empty-content">
        <span>您还未登录，请先登录</span>
      </div>
      <template v-else-if="projectCategoriesTabs && projectCategoriesTabs.length">
        <gjw-tabs
            :tabs="projectCategoriesTabs"
            size="small"
            class="selection-list-tabs"
            @tab-clicked="tabChange"
            @contentSizeChanged="getContentHeight"
        >
          <template v-for="item in projectCategoriesTabs" #[`content-${item.id}`]="{tab}">
            <selected-table
                ref="selectedTable"
                :key="item.id"
                :category-type="['fan', 'pump'].includes(item.id) ? item.id : 'basic'"
                :category-code="tab.code"
                :columns="tableCols"
                :table-data="projectProducts"
                :table-max-height="tableBoxHeight"
            />
          </template>
        </gjw-tabs>
      </template>

      <div v-else class="empty-content no-data">
        <img src="../../assets/image/pic-empty-car.png" class="img" alt=""/>
        <span>当前账号没有选中任何产品，快去【产品列表】点击 “选中” 试试吧。</span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Utils from '@/core/utils'
import productUtils from '@/core/productUtils'
import BackHeader from '@/components/business/BackHeader'
import GjwTabs from '@/components/lib/GjwTabs'
import SelectedTable from './components/SelectedTable'
import gcsReplayRecordMixin from '@/mixins/gcsReplayRecordMixin'

export default {
  name: 'SelectionList',
  components: {
    BackHeader,
    GjwTabs,
    SelectedTable,
  },
  mixins: [gcsReplayRecordMixin],
  data() {
    return {
      // 接口请求完毕后，统一显示内容，防止页面闪换内容
      pageLoadOver: false,
      tableBoxHeight: 0,
      activeTab: {},
      classificationProps: [],
      fanProductCols: [
        {
          label: '设计风量(m³/h)',
          prop: '设计风量',
          minWidth: '70px',
          fmtValue: (row, col, cell) => this.fmtValue(row, col, cell, true),
        }, {
          label: '设计全压(Pa)',
          prop: '设计全压',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '转速(rpm)',
          prop: 'speed',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '运行风量(m³/h)',
          prop: '运行风量',
          minWidth: '70px',
          fmtValue: this.fmtValue,
        }, {
          label: '运行全压(Pa)',
          prop: '运行全压',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '运行全压效率(%)',
          prop: '运行全压效率',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '单位风量耗功率(W/(m³/h))',
          prop: 'powerConsumptionPerAirVolume',
          width: '96px',
          toolTip: {
            name: '单位风量耗功率(W/(m³/h))',
            content: '=设计全压/(3600*0.855*运行全压效率/100)',
            iconClass: 'gjw-iconfont gjw-icon-tishi1'
          },
          fmtValue: this.fmtValue,
        }, {
          label: '电机功率(kW)',
          prop: 'power',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '噪声(dB(A))',
          prop: 'noise',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }
      ],
      pumpProductCols: [
        {
          label: '设计流量(m³/h)',
          prop: '设计流量',
          minWidth: '70px',
          fmtValue: (row, col, cell) => this.fmtValue(row, col, cell, true),
        }, {
          label: '设计扬程(m)',
          prop: '设计扬程',
          minWidth: '64px',
        }, {
          label: '转速(rpm)',
          prop: 'speed',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '运行转速(rpm)',
          prop: '运行转速',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '运行流量(m³/h)',
          prop: '运行流量',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '运行扬程(m)',
          prop: '运行扬程',
          minWidth: '60px',
          fmtValue: this.fmtValue,
        }, {
          label: '运行效率(%)',
          prop: '运行效率',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }, {
          label: '电机功率(kW)',
          prop: 'power',
          minWidth: '64px',
          fmtValue: this.fmtValue,
        }
      ],
    }
  },
  computed: {
    ...mapGetters({
      platform: 'platform',
      user: 'user/user',
      userToken: 'user/userToken',
      project: 'selectionList/project',
      projectCategoriesAgg: 'selectionList/projectCategoriesAgg',
      projectCategoriesTabs: 'selectionList/projectCategoriesTabs',
      projectProducts: 'selectionList/projectProducts',
    }),
    isLogin() {
      return (this.platform === 'web' && this.user.strId) || (this.platform !== 'web' && this.userToken)
    },
    // 基础品类的设计属性列
    basicDesignCols() {
      if (['fan', 'pump'].includes(this.activeTab.id)) {
        return []
      }
      const filterValue = (value) => !!value
      const designProps = productUtils.getAllPropertiesByCode(this.classificationProps, 'isPerformanceFilter', filterValue)
      return designProps.sort((i, j) => i.sort - j.sort).map((dp, index) => {
        const labelText = dp.unitSymbol ? `设计${dp.name}(${dp.unitSymbol})` : `设计${dp.name}`
        return {
          label: labelText,
          prop: `design_${dp.code}`,
          minWidth: Utils.calcTextWidth(labelText) + 10 + 'px',
          fmtValue: (row, col, cell) => this.fmtValue(row, col, cell, index === 0),
        }
      })
    },
    // 基础品类的列表展示属性列
    basicDisplayCols() {
      if (['fan', 'pump'].includes(this.activeTab.id)) {
        return []
      }
      const filterValue = (value) => {
        return value !== null && value !== undefined && value !== ''
      }
      const displayProps = productUtils.getAllPropertiesByCode(this.classificationProps, 'isListDisplay', filterValue)
      return displayProps.sort((i, j) => i.sort - j.sort).map((dp) => {
        const labelText = dp.unitSymbol ? `${dp.name}(${dp.unitSymbol})` : `${dp.name}`
        return {
          label: labelText,
          prop: dp.code,
          minWidth: Utils.calcTextWidth(labelText) + 10 + 'px',
          fmtValue: this.fmtValue,
        }
      })
    },
    tableCols() {
      const activeTab = this.activeTab || {}
      switch (activeTab.id) {
        case 'fan': return this.fanProductCols
        case 'pump': return this.pumpProductCols
        default: return [...this.basicDesignCols, ...this.basicDisplayCols]
      }
    },
  },
  watch: {
    // 当项目id更新后，更新品类聚合信息及产品信息
    'project.id': {
      async handler(val) {
        if (val || val === 0) {
          await this.loadProjectCategoriesAgg()
          if (this.projectCategoriesAgg && this.projectCategoriesAgg.length) {
            this.activeTab = this.projectCategoriesTabs[0]
          }
        }
      },
      immediate: true
    },
    'activeTab.code': {
      handler(val) {
        if (val || val === 0) {
          this.setPageNo(1)
          const params = { gjw_category_code: val }
          this.setTableLoading(true)
          // 获取品类下属性
          this.getProperties(this.activeTab.code).then(() => {
            // 获取品类下tableData
            this.loadProjectProducts(params)
          }).catch(() => {
            this.$message.error('获取数据失败')
          })
          // 获取参数定义
          this.loadDefinitions(params)
        }
      }
    },
  },
  created() {
    setTimeout(() => {
      this.pageLoadOver = true
    }, 800)
  },
  mounted() {
    // 进入页面，返回actionResult，结束回放
    window.replaySelectionListCallback && window.replaySelectionListCallback()
  },
  methods: {
    ...mapMutations({
      setPageNo: 'selectionList/setPageNo',
      setTableLoading: 'selectionList/setTableLoading',
      setCategoryAllProps: 'selectionList/setCategoryAllProps',
    }),
    ...mapActions({
      loadUserInfo: 'user/loadUserInfo',
      loadCurrentProject: 'selectionList/loadCurrentProject',
      loadProjectCategoriesAgg: 'selectionList/loadProjectCategoriesAgg',
      loadProjectProducts: 'selectionList/loadProjectProducts',
      loadDefinitions: 'shareParams/loadDefinitions',
    }),
    // 获取某品类下的所有属性
    getProperties(classificationCode) {
      return new Promise((resolve, reject) => {
        this.$api.classification.getClassificationProperties(classificationCode).then((data) => {
          this.classificationProps = data || []
          this.setCategoryAllProps(this.classificationProps)
          resolve()
        }).catch((err) => {
          reject(err)
        })
      })
    },
    // 根据单双速，处理值的显示
    fmtValue(row, col, cell, isFirstSpan) {
      const colProp = col.property
      if (row.isOffline && isFirstSpan) {
        // 已下架产品从第一个设计属性列开始向后合并单元格，所以相应cell的显示值要变化
        return `该产品已下架或删除`
      }
      if (row.speedControl === 'DOUBLE_SPEED' && !colProp.startsWith('设计') && !colProp.startsWith('运行')) {
        // 产品属性数据需要拼接
        const highKey = `${colProp}High`
        const lowKey = `${colProp}Low`
        return (row[highKey] || row[lowKey]) ? `${this.$fmtValue(row[highKey])}/${this.$fmtValue(row[lowKey])}` : '-/-'
      } else {
        return this.$fmtValue(row[colProp], '-')
      }
    },
    tabChange(tab) {
      this.activeTab = tab
    },
    getContentHeight(obj) {
      this.tableBoxHeight = Number(obj.newValue.toFixed(0)) - 40
    },
  },
}
</script>

<style lang="less" scoped>
  .selection-list-page{
    display: flex;
    flex-direction: column;
    .selection-list-tabs{
      flex: 1;
    }
  }
  .web-page-header{
    font-size: 16px;
    padding: 10px 0 6px;
  }
  .empty-content{
    margin-top: 200px;
    text-align: center;
    color: @theme-grey-3;
  }
  .no-data{
    margin-top: 100px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .img{
      width: 186px;
      margin-bottom: 10px;
    }
  }
</style>
